import { ShipTierNames } from "@voidex/contracts-ts";
import { motion } from "framer-motion";
import { formatEther } from "viem";
import { FarmOutcome, FarmOutcomeColors, FarmOutcomeNames } from "../../hooks";
import type { ResolvedMission } from "../../hooks";
import { ShipIcon } from "../shared/ShipIcon";

interface MissionOutcomeProps {
	mission: ResolvedMission;
	planetName: string;
	planetColor: string;
	onClose: () => void;
}

const OUTCOME_ICONS: Record<FarmOutcome, string> = {
	[FarmOutcome.Destroyed]: "\u2620", // skull
	[FarmOutcome.Poor]: "\u25C7", // diamond outline
	[FarmOutcome.Standard]: "\u25C6", // diamond filled
	[FarmOutcome.Rich]: "\u2726", // four-pointed star
	[FarmOutcome.Jackpot]: "\u2728", // sparkles
};

export function MissionOutcome({ mission, planetName, planetColor, onClose }: MissionOutcomeProps) {
	const outcomeName = FarmOutcomeNames[mission.outcome];
	const outcomeColor = FarmOutcomeColors[mission.outcome];
	const icon = OUTCOME_ICONS[mission.outcome];
	const isDestroyed = mission.outcome === FarmOutcome.Destroyed;

	const formattedPoints = Number.parseFloat(formatEther(mission.pointsEarned)).toFixed(2);

	return (
		<motion.div
			className={`mission-outcome ${isDestroyed ? "mission-outcome--destroyed" : ""}`}
			initial={{ opacity: 0, scale: 0.8 }}
			animate={{ opacity: 1, scale: 1 }}
			transition={{ type: "spring", damping: 20, stiffness: 300, delay: 0.1 }}
		>
			<div
				className="mission-outcome__glow"
				style={{ background: `radial-gradient(circle, ${outcomeColor}22 0%, transparent 70%)` }}
			/>

			<div className="mission-outcome__icon" style={{ color: outcomeColor }}>
				{icon}
			</div>

			<h2 className="mission-outcome__title" style={{ color: outcomeColor }}>
				{outcomeName}
			</h2>

			<p className="mission-outcome__subtitle">
				Mission to <span style={{ color: planetColor }}>{planetName}</span> complete
			</p>

			<div className="mission-outcome__stats">
				<div className="mission-outcome__stat">
					<span className="mission-outcome__stat-label">Ship</span>
					<span
						className="mission-outcome__stat-value"
						style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
					>
						<ShipIcon tier={mission.shipTier} size="sm" />
						{isDestroyed ? (
							<span className="mission-outcome__stat-value--lost">
								{ShipTierNames[mission.shipTier]} — Destroyed
							</span>
						) : (
							ShipTierNames[mission.shipTier]
						)}
					</span>
				</div>
				{!isDestroyed && (
					<div className="mission-outcome__stat">
						<span className="mission-outcome__stat-label">Points Earned</span>
						<span
							className="mission-outcome__stat-value mission-outcome__stat-value--earned"
							style={{ color: outcomeColor }}
						>
							+{formattedPoints}
						</span>
					</div>
				)}
			</div>

			<button type="button" className="mission-outcome__btn" onClick={onClose}>
				Close
			</button>
		</motion.div>
	);
}
